<template>
  <button dusk="report-issue-button" type="button" @click="show = true" :class="{'shadow-xl': show}">
    <slot />
    <portal v-if="show" to="dropdown">
      <div>
        <div class="fixed z-40 inset-0" @click="show = false" />
        <section
          dusk="report-issue-dropdown"
          ref="dropdown"
          role="tooltip"
          class="issue-dropdown w-80 drop-down-content z-50 bg-white"
          @click.stop
        >
          <form method="post" @submit.prevent="submitIssueReport">
            <header class="title-block flex items-center justify-between h-14 px-6 bg-platinumHot">
              <h3 class="font-medium text-base text-yank select-none flex items-center">
                <span>What's the issue?</span>
                <FlagIcon class="ml-2 h-4 text-payneMiddle" />
              </h3>
              <button @click="show = false" type="button" dusk="cancel-report-issue-button" class="cancel-button text-xs text-payneFire select-none focus:outline-none">
                Cancel
              </button>
            </header>

            <main>
              <div class="mx-3 border-b border-platinumFire">
                <label class="px-3 py-5 flex items-center justify-between cursor-pointer relative">
                  <input class="hidden" type="checkbox" name="reason" value="copyright" v-model="form.reason">
                  <span class="text-sm font-medium text-payne select-none leading-none">Copyrighting Issue</span>
                  <span class="absolute inset-y-0 mr-3 right-0 flex items-center justify-center">
                    <CheckedIcon v-if="form.reason.includes('copyright')" class="h-8 text-aquamarine" />
                  </span>
                </label>
              </div>
              <div class="mx-3 border-b border-platinumFire">
                <label class="px-3 py-5 flex items-center justify-between cursor-pointer relative">
                  <input class="hidden" type="checkbox" name="reason" value="download" v-model="form.reason">
                  <span class="text-sm font-medium text-payne select-none leading-none">Downloading Issue</span>
                  <span class="absolute inset-y-0 mr-3 right-0 flex items-center justify-center">
                    <CheckedIcon v-if="form.reason.includes('download')" class="h-8 text-aquamarine" />
                  </span>
                </label>
              </div>
              <div class="mx-3 border-b border-platinumFire">
                <label class="px-3 py-5 flex items-center justify-between cursor-pointer relative">
                  <input class="hidden" type="checkbox" name="reason" value="grammar-spelling" v-model="form.reason">
                  <span class="text-sm font-medium text-payne select-none leading-none">Grammar / Spelling Issue</span>
                  <span class="absolute inset-y-0 mr-3 right-0 flex items-center justify-center">
                    <CheckedIcon v-if="form.reason.includes('grammar-spelling')" class="h-8 text-aquamarine" />
                  </span>
                </label>
              </div>
              <div class="mx-3">
                <label class="px-3 pt-5 pb-4 flex items-center justify-between cursor-pointer relative">
                  <input class="hidden" type="checkbox" name="reason" value="other" v-model="form.reason">
                  <span class="text-sm font-medium text-payne select-none leading-none">Describe your issue(s)</span>
                  <span class="absolute inset-y-0 mr-3 right-0 flex items-center justify-center">
                    <CheckedIcon v-if="form.reason.includes('other')" class="h-8 text-aquamarine" />
                  </span>
                </label>
              </div>

              <ShowDetails :pose="form.reason.length ? 'visible' : 'hidden'">
                <div>
                  <label class="sr-only" for="details">Issue Details</label>
                  <div class="px-3">
                    <textarea name="details" id="details" rows="3" class="resize-none border border-platinumFire rounded-xl p-2 text-sm text-yank focus:outline-none w-full focus:ring-cosmosHot hover:border-cosmosHot focus:border-cosmosHot" placeholder="Type in the issue you found..." v-model="form.details"></textarea>
                  </div>
                </div>
              </ShowDetails>
            </main>

            <footer class="px-6 mb-4 mt-6">
              <button type="submit" class="bg-payneHot h-12 rounded-xl text-white font-semibold text-sm border border-payneFire w-full hover:opacity-90 focus:outline-none disabled:opacity-50 flex items-center justify-center" :disabled="! formIsReady || working">
                <loading-icon v-if="working" class="h-4" />
                <span v-else>Submit</span>
              </button>
            </footer>
          </form>
          <div id="arrow" data-popper-arrow></div>
        </section>
      </div>
    </portal>
  </button>
</template>

<script>
  import { createPopper } from '@popperjs/core'
  import LoadingIcon from '@/components/svg/LoadingIcon'
  import FlagIcon from '@/components/svg/FlagIcon'
  import CheckedIcon from '@/components/svg/CheckedIcon'
  import posed from 'vue-pose'

  export default {
    props: {
      placement: {
        type: String,
        default: 'bottom-start',
      },

      boundary: {
        type: String,
        default: 'scrollParent',
      },

      media: Object,
    },

    components: {
      LoadingIcon,
      FlagIcon,
      CheckedIcon,
      ShowDetails: posed.div({
        visible: {
          opacity: 1,
          height: `auto`,
          overflow: 'auto',
        },
        hidden: {
          opacity: 0,
          height: 0,
          overflow: 'hidden',
        }
      })
    },

    data() {
      return {
        show: false,
        working: false,
        form: {
          reason: [],
          details: '',
        }
      }
    },

    watch: {
      show(show) {
        if (show) {
          this.$nextTick(() => {
            this.popper = createPopper(this.$el, this.$refs.dropdown, {
              placement: this.placement,
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: this.boundary,
                  }
                },
                {
                  name: 'offset',
                  options: {
                    offset: [0, 8],
                  }
                }
              ]
            })
          })
        } else if (this.popper) {
          setTimeout(() => this.popper.destroy(), 100)
          this.reset()
        }
      },
    },

    computed: {
      formIsReady() {
        if (! this.form.reason.length) {
          return false;
        }

        if (! this.form.details.length) {
          return false;
        }

        return true;
      },
    },

    methods: {
      handleEscapeKey(e) {
        if (e.keyCode === 27) {
          this.show = false
        }
      },

      reset() {
        this.form.reason = []
        this.form.details = ''
      },

      submitIssueReport() {
        this.$inertia.post(this.$route('media.issues.store', this.media.id), this.form, {
          preserveScroll: true,
          preserveState: true,
          onStart: () => this.working = true,
          onSuccess: () => this.show = false,
          onFinish: () => this.working = false,
        })
      }
    },

    mounted() {
      document.addEventListener('keydown', this.handleEscapeKey)
    },

    beforeDestroy() {
      document.removeEventListener('keydown', this.handleEscapeKey)
    }
  }
</script>

<style lang="scss" scoped>
  .issue-dropdown {
    box-shadow: 0px 4px 22px rgba(55, 54, 77, 0.15);
  }

  #arrow,
  #arrow::before {
    position: absolute;
    width: 8px;
    height: 8px;
    z-index: -1;
  }

  #arrow::before {
    @apply bg-platinumMild;
    content: '';
    transform: rotate(45deg);
  }

  [data-popper-placement^='top'] > #arrow {
    bottom: -4px;
  }

  [data-popper-placement^='bottom'] > #arrow {
    top: -4px;
  }

  [data-popper-placement^='left'] > #arrow {
    right: -4px;
  }

  [data-popper-placement^='right'] > #arrow {
    left: -4px;
  }
</style>
